<template>
  <div>
    <div class="tillbakalank">
      <button class="prevBtn btn-link" type="button" @click="$emit('back')">
        <svg-icon icon-name="pil-vanster-ny" />
        <span>Tillbaka till förhandsgranskning</span>
      </button>
    </div>

    <div v-if="saveError" class="alert alert-danger alert-dismissible" role="alert">
      <button type="button" class="close" @click="saveError = null" aria-label="Stäng">
        <span aria-hidden="true">&times;</span>
      </button>
      <strong>Fel!</strong> {{ saveError }}
    </div>

    <div v-if="saveSuccess" class="alert alert-success alert-dismissible" role="alert">
      <button type="button" class="close" @click="saveSuccess = null" aria-label="Stäng">
        <span aria-hidden="true">&times;</span>
      </button>
      <strong>Klart!</strong> {{ saveSuccess }}
    </div>

    <h1 class="rubrik-steg">Granska tips</h1>

    <div id="form" class="col-md-8">
      <template v-if="tips.IsPublished">
        <div class="infosektion">
          <div class="infosektion-1">
            <div class="form-group">
              <label>Status: </label>
              <span :class="['infosektion-1--status', tips.Status]">{{ statusLabel }}</span>
            </div>
            <div class="form-group">
              <label>Publicerad av:</label>
              <span>{{ tips.SenastÄndradAv }}</span>
            </div>
          </div>
          <div class="infosektion-2">
            <div class="form-group">
              <label>Senast publicerat: </label>
              <span>{{ tips.PubliceradDatumTid }}</span>
            </div>
            <div class="form-group">
              <label>Utgångsdatum: </label>
              <span>{{ tips.UtgårDatum }}</span>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="form-group">
          <label for="edit-status">Ändra status</label>
          <select id="edit-status" v-model="form.Status" class="form-control">
            <option v-for="opt in tips.StatusOptions" :key="opt.Value" :value="opt.Value">
              {{ opt.Text }}
            </option>
          </select>
        </div>
      </template>

      <div class="form-section">
        <h2>Om materialet</h2>

        <div class="form-group">
          <label for="edit-rubrik">Rubrik för materialet</label>
          <input id="edit-rubrik" type="text" class="form-control" v-model="form.Rubrik" autocomplete="off" />
        </div>

        <div class="form-group">
          <label for="edit-lankMaterial">Länk till materialet</label>
          <input id="edit-lankMaterial" type="text" class="form-control" v-model="form.LänkMaterial" autocomplete="off" />
        </div>

        <div v-if="form.LänkMaterial" class="form-group">
          <label>Besök länken</label>
          <p>
            <a :href="form.LänkMaterial" target="_blank" rel="noreferrer">{{ form.LänkMaterial }}</a>
          </p>
        </div>

        <div class="form-group">
          <label for="edit-ingress">Kort ingress</label>
          <textarea id="edit-ingress" class="form-control" v-model="form.Ingress" rows="3" autocomplete="off"></textarea>
        </div>

        <div :class="['form-group', {'has-error': errors.beskrivning}]" id="beskrivning">
          <label id="beskrivning-label">Beskrivning av materialet</label>
          <div class="error-msg">
            <svg-icon icon-name="varning-varning" class="error-msg-icon" />
            <span>{{ errors.beskrivning }}</span>
          </div>
          <html-editor
            :model-value="form.BeskrivningHtml"
            @update:model-value="val => { form.BeskrivningHtml = val; clearError('beskrivning'); }"
            aria-labelledby="beskrivning-label"
          />
        </div>

        <div class="form-group">
          <label for="edit-framtagare">Vem har tagit fram materialet? (Skriv namn eller organisation)</label>
          <input id="edit-framtagare" type="text" class="form-control" v-model="form.Framtagare" autocomplete="off" />
        </div>

        <div :class="['form-group', {'has-error': errors.del1Granskad}]" id="granskningdel1">
          <label>Granskning</label>
          <div class="error-msg">
            <svg-icon icon-name="varning-varning" class="error-msg-icon" />
            <span>{{ errors.del1Granskad }}</span>
          </div>
          <div class="checkboxItem">
            <input id="edit-del1" type="checkbox" v-model="form.Del1Granskad" class="chk" @change="clearError('del1Granskad')" />
            <span class="checkmark"></span>
            <label for="edit-del1" class="granskad-label">Del 1 granskad</label>
          </div>
        </div>
      </div>

      <div class="form-section">
        <h2>Kategorisera materialet</h2>

        <div :class="['category-checks', {'has-error': errors.nyckelord}]" id="nyckelord">
          <label>Välj nyckelord</label>
          <div class="error-msg">
            <svg-icon icon-name="varning-varning" class="error-msg-icon" />
            <span>{{ errors.nyckelord }}</span>
          </div>
          <div :class="form.NyckelordOptions.length > 10 ? 'dubbel-kolumn' : ''">
            <div v-for="opt in form.NyckelordOptions" :key="opt.Id" class="checkboxItem">
              <input :id="`nyckelord-${opt.Id}`" type="checkbox" v-model="opt.Selected" @change="clearError('nyckelord')" />
              <span class="checkmark"></span>
              <label :for="`nyckelord-${opt.Id}`">{{ opt.Text }}</label>
            </div>
          </div>
        </div>

        <div :class="['category-checks', {'has-error': errors.typer}]" id="typer">
          <label>Välj typ av media</label>
          <div class="error-msg">
            <svg-icon icon-name="varning-varning" class="error-msg-icon" />
            <span>{{ errors.typer }}</span>
          </div>
          <div :class="form.TypOptions.length > 10 ? 'dubbel-kolumn' : ''">
            <div v-for="opt in form.TypOptions" :key="opt.Id" class="checkboxItem">
              <input :id="`typ-${opt.Id}`" type="checkbox" v-model="opt.Selected" @change="clearError('typer')" />
              <span class="checkmark"></span>
              <label :for="`typ-${opt.Id}`">{{ opt.Text }}</label>
            </div>
          </div>
        </div>

        <div :class="['form-group', {'has-error': errors.del2Granskad}]" id="granskningdel2">
          <label>Granskning</label>
          <div class="error-msg">
            <svg-icon icon-name="varning-varning" class="error-msg-icon" />
            <span>{{ errors.del2Granskad }}</span>
          </div>
          <div class="checkboxItem">
            <input id="edit-del2" type="checkbox" v-model="form.Del2Granskad" class="chk" @change="clearError('del2Granskad')" />
            <span class="checkmark"></span>
            <label for="edit-del2" class="granskad-label">Del 2 granskad</label>
          </div>
        </div>
      </div>

      <div class="form-section">
        <h2>Tipsarens kontaktuppgifter</h2>

        <div class="form-group">
          <label for="edit-namn">Namn</label>
          <input id="edit-namn" type="text" class="form-control" v-model="form.InskickadAvNamn" autocomplete="off" />
        </div>

        <div class="form-group">
          <label for="edit-epost">Epostadress</label>
          <input id="edit-epost" type="text" class="form-control" v-model="form.InskickadAvEpost" autocomplete="off" />
        </div>
      </div>

      <div class="form-buttons">
        <template v-if="tips.IsPublished">
          <button class="btn btn-default" type="button" @click="showUnpublishModal = true" :disabled="isSaving">Avpublicera</button>
          <button class="btn btn-primary publish-button" type="button" @click="onPublish" :disabled="isSaving">
            {{ isSaving ? 'Sparar...' : 'Publicera' }}
          </button>
        </template>
        <template v-else>
          <button class="btn btn-default" type="button" @click="showDeleteModal = true" :disabled="isSaving">Ta bort</button>
          <button class="btn btn-default" type="button" @click="onSave" :disabled="isSaving">
            {{ isSaving ? 'Sparar...' : 'Spara' }}
          </button>
          <button class="btn btn-primary publish-button" type="button" @click="onPublish" :disabled="isSaving">
            {{ isSaving ? 'Publicerar...' : 'Publicera' }}
          </button>
        </template>
      </div>
    </div>

    <!-- Delete confirmation modal -->
    <confirm-modal
      v-if="showDeleteModal"
      title="Vill du ta bort tipset?"
      confirm-label="Ja, ta bort tipset"
      cancel-label="Nej, behåll tipset"
      :confirm-disabled="isSaving"
      @confirm="onDelete"
      @close="showDeleteModal = false"
    >
      <p>Är du säker på att du vill ta bort tipset? Tipset kommer då att försvinna och inte finnas kvar.</p>
    </confirm-modal>

    <!-- Unpublish confirmation modal -->
    <confirm-modal
      v-if="showUnpublishModal"
      title="Vill du avpublicera tipset?"
      confirm-label="Ja, avpublicera tipset"
      cancel-label="Avbryt"
      :confirm-disabled="isSaving"
      @confirm="onUnpublish"
      @close="showUnpublishModal = false"
    >
      <p>Är du säker på att du vill avpublicera tipset? Tipset kommer då inte att visas för besökare. Tipset kommer att finnas kvar i 3 månader och du kan då ompublicera det igen om du skulle vilja det.</p>
    </confirm-modal>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, reactive, ref, watch } from 'vue';
import SvgIcon from '../components/svgicon.vue';
import HtmlEditor from '../components/HtmlEditor.vue';
import {
  CheckboxOptionDto,
  HanteraTipsEditDto,
  deleteTips,
  saveTips,
  unpublishTips,
} from './hanteraTipsSidaService';
import ConfirmModal from '../components/ConfirmModal.vue';

export default defineComponent({
  name: 'HanteraTipsEdit',
  components: { SvgIcon, HtmlEditor, ConfirmModal },
  props: {
    tips: { type: Object as PropType<HanteraTipsEditDto>, required: true },
    contentId: { type: Number, required: true },
  },
  emits: ['back', 'done'],
  setup(props, { emit }) {
    const isSaving = ref(false);
    const saveError = ref<string | null>(null);
    const saveSuccess = ref<string | null>(null);
    const showDeleteModal = ref(false);
    const showUnpublishModal = ref(false);

    const form = reactive({
      Rubrik: '',
      LänkMaterial: '',
      Ingress: '',
      BeskrivningHtml: '',
      Framtagare: '',
      Del1Granskad: false,
      Del2Granskad: false,
      Status: '',
      InskickadAvNamn: '',
      InskickadAvEpost: '',
      NyckelordOptions: [] as CheckboxOptionDto[],
      TypOptions: [] as CheckboxOptionDto[],
    });

    // Populate form immediately from props before watch runs
    Object.assign(form, {
      Rubrik: props.tips.Rubrik ?? '',
      LänkMaterial: props.tips.LänkMaterial ?? '',
      Ingress: props.tips.Ingress ?? '',
      BeskrivningHtml: props.tips.BeskrivningHtml ?? '',
      Framtagare: props.tips.Framtagare ?? '',
      Del1Granskad: props.tips.Del1Granskad,
      Del2Granskad: props.tips.Del2Granskad,
      Status: props.tips.Status,
      InskickadAvNamn: props.tips.InskickadAvNamn ?? '',
      InskickadAvEpost: props.tips.InskickadAvEpost ?? '',
      NyckelordOptions: props.tips.NyckelordOptions.map(o => ({ ...o })),
      TypOptions: props.tips.TypOptions.map(o => ({ ...o })),
    });

    const errors = reactive<Record<string, string>>({
      del1Granskad: '',
      del2Granskad: '',
      nyckelord: '',
      typer: '',
      beskrivning: '',
    });

    watch(() => props.tips, (newTips) => {
      Object.assign(form, {
        Rubrik: newTips.Rubrik ?? '',
        LänkMaterial: newTips.LänkMaterial ?? '',
        Ingress: newTips.Ingress ?? '',
        BeskrivningHtml: newTips.BeskrivningHtml ?? '',
        Framtagare: newTips.Framtagare ?? '',
        Del1Granskad: newTips.Del1Granskad,
        Del2Granskad: newTips.Del2Granskad,
        Status: newTips.Status,
        InskickadAvNamn: newTips.InskickadAvNamn ?? '',
        InskickadAvEpost: newTips.InskickadAvEpost ?? '',
        NyckelordOptions: newTips.NyckelordOptions.map(o => ({ ...o })),
        TypOptions: newTips.TypOptions.map(o => ({ ...o })),
      });
      Object.keys(errors).forEach(k => errors[k] = '');
    });

    const statusLabel = computed(() => {
      if (props.tips.Status === 'UtgårSnart') return 'Utgår snart';
      return props.tips.Status;
    });

    function clearError(field: string) {
      errors[field] = '';
    }

    function validateForPublish(): boolean {
      errors.del1Granskad = form.Del1Granskad ? '' : 'Du måste bekräfta granskning av denna del';
      errors.del2Granskad = form.Del2Granskad ? '' : 'Du måste bekräfta granskning av denna del';
      errors.nyckelord = form.NyckelordOptions.some(o => o.Selected) ? '' : 'Välj minst ett nyckelord';
      errors.typer = form.TypOptions.some(o => o.Selected) ? '' : 'Välj minst en typ';
      errors.beskrivning = form.BeskrivningHtml?.replace(/<[^>]+>/g, '').trim()
        ? '' : 'Du måste fylla i en beskrivning';

      return !errors.del1Granskad && !errors.del2Granskad
        && !errors.nyckelord && !errors.typer && !errors.beskrivning;
    }

    function buildRequest(publish: boolean) {
      return {
        ContentId: props.contentId,
        TipsContentId: props.tips.TipsContentId,
        Rubrik: form.Rubrik,
        LänkMaterial: form.LänkMaterial,
        Ingress: form.Ingress,
        BeskrivningHtml: form.BeskrivningHtml,
        Framtagare: form.Framtagare,
        Del1Granskad: form.Del1Granskad,
        Del2Granskad: form.Del2Granskad,
        Status: form.Status,
        InskickadAvNamn: form.InskickadAvNamn,
        InskickadAvEpost: form.InskickadAvEpost,
        Nyckelord: form.NyckelordOptions.filter(o => o.Selected).map(o => o.Id),
        Typ: form.TypOptions.filter(o => o.Selected).map(o => o.Id),
        Publish: publish,
      };
    }

    async function onSave() {
      isSaving.value = true;
      saveError.value = null;
      saveSuccess.value = null;
      try {
        await saveTips(buildRequest(false));
        saveSuccess.value = 'Tipset har sparats.';
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch {
        saveError.value = 'Det gick inte att spara tipset. Försök igen senare.';
      } finally {
        isSaving.value = false;
      }
    }

    async function onPublish() {
      if (!validateForPublish()) {
        // Scroll to first validation error
        const firstError = document.querySelector('.has-error');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }
      isSaving.value = true;
      saveError.value = null;
      saveSuccess.value = null;
      try {
        await saveTips(buildRequest(true));
        emit('done', 'Tipset har publicerats.');
      } catch {
        saveError.value = 'Det gick inte att publicera tipset. Försök igen senare.';
        isSaving.value = false;
      }
    }

    async function onDelete() {
      isSaving.value = true;
      showDeleteModal.value = false;
      try {
        await deleteTips(props.contentId, null, props.tips.TipsContentId);
        emit('done', 'Tipset har tagits bort.');
      } catch {
        saveError.value = 'Det gick inte att ta bort tipset. Försök igen senare.';
        isSaving.value = false;
      }
    }

    async function onUnpublish() {
      isSaving.value = true;
      showUnpublishModal.value = false;
      try {
        await unpublishTips(props.contentId, props.tips.TipsContentId);
        emit('done', 'Tipset har avpublicerats.');
      } catch {
        saveError.value = 'Det gick inte att avpublicera tipset. Försök igen senare.';
        isSaving.value = false;
      }
    }

    return {
      form,
      errors,
      isSaving,
      saveError,
      saveSuccess,
      showDeleteModal,
      showUnpublishModal,
      statusLabel,
      clearError,
      onSave,
      onPublish,
      onDelete,
      onUnpublish,
    };
  },
});
</script>
